/**
 * The big instruction sign shown while navigating: arrow glyph, distance
 * countdown, road name / exit text, the lane bar, and a "then" preview of the
 * following turn.
 *
 * Imminence model (the "very very clear" requirement):
 *  - > threshold: normal sign.
 *  - inside threshold: the card enters `is-imminent` - it grows, the accent bar
 *    animates, the arrow pulses. Threshold scales with speed so at highway pace
 *    it fires earlier.
 *  - lane bar renders whenever lane data exists, and its valid lanes flash in
 *    the imminent state when a lane CHANGE is actually required (your current
 *    lane can't serve the maneuver - we can't know your lane from GPS, so we
 *    flash when only a strict subset of lanes is valid).
 */

import type { NavProgress } from '../core/types';
import type { UnitSystem } from '../core/settings';
import { formatDistance } from '../core/format';
import { arrowSvg, glyphForManeuver, laneChipSvg } from './arrows';
import { t } from '../core/i18n';

export class ManeuverCard {
  readonly el: HTMLElement;
  private lastRenderKey = '';

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'maneuver-card';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  hide(): void {
    this.el.hidden = true;
    this.lastRenderKey = '';
  }

  update(progress: NavProgress, units: UnitSystem, speedMs: number): void {
    const { step, banner, lanes, distanceToManeuver } = progress;
    const m = step.maneuver;

    // Imminent when within ~12 s of travel or 80 m, whichever is larger.
    const imminentAt = Math.max(80, speedMs * 12);
    const imminent = distanceToManeuver <= imminentAt;

    const primaryText = banner?.primary.text ?? m.instruction;
    const secondaryText = banner?.secondary?.text ?? '';
    const glyph = glyphForManeuver(
      banner?.primary.type ?? m.type,
      banner?.primary.modifier ?? m.modifier,
    );
    const distText = formatDistance(distanceToManeuver, units);

    const next = progress.nextStep;
    const thenGlyph =
      next && distanceToManeuver < 400 && step.distance < 700
        ? glyphForManeuver(next.maneuver.type, next.maneuver.modifier)
        : null;

    // Lane change genuinely required only when some lanes are invalid.
    const laneChangeNeeded = !!lanes && lanes.some((l) => !l.valid) && lanes.some((l) => l.valid);

    // Cheap dirty-check so we only touch the DOM when content changes;
    // distance ticks every fix but the rest is stable per step.
    const key = [
      glyph,
      primaryText,
      secondaryText,
      distText,
      imminent,
      thenGlyph,
      lanes ? lanes.map((l) => `${l.valid}:${l.indications.join('|')}`).join(',') : '',
    ].join('~');
    if (key === this.lastRenderKey) return;
    this.lastRenderKey = key;

    this.el.hidden = false;
    this.el.classList.toggle('is-imminent', imminent);
    this.el.classList.toggle('has-lane-change', laneChangeNeeded && imminent);

    this.el.innerHTML = `
      <div class="maneuver-card__main">
        <div class="maneuver-card__arrow">${arrowSvg(glyph, 'maneuver-card__glyph')}</div>
        <div class="maneuver-card__text">
          <div class="maneuver-card__distance">${distText}</div>
          <div class="maneuver-card__primary">${escapeHtml(primaryText)}</div>
          ${secondaryText ? `<div class="maneuver-card__secondary">${escapeHtml(secondaryText)}</div>` : ''}
        </div>
        ${
          thenGlyph
            ? `<div class="maneuver-card__then"><span>${t('nav.then')}</span>${arrowSvg(thenGlyph, 'maneuver-card__then-glyph')}</div>`
            : ''
        }
      </div>
      ${
        lanes?.length
          ? `<div class="lane-bar" role="img" aria-label="Lane guidance">${lanes
              .map((l) => laneChipSvg(l.indications, l.valid_indication, l.valid))
              .join('')}</div>`
          : ''
      }
    `;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
